import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, CheckCircle, Circle, Calendar, Clock, MapPin, Link as LinkIcon, Plus, Pencil, Trash2, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, AlertCircle, BookOpen, Search, Grid3X3, List, Bell, X, Send, Paperclip, Image as ImageIcon, File, Download } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MentionInput } from '../components/MentionInput';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { saveAs } from 'file-saver';
import { formatPhoneNumber, formatPhoneInput } from '../utils/phoneFormat';
import { linkifyText } from '../utils/linkify';
import { formatDateTimeShort } from '../utils/dateFormat';

// Import constants and components from extracted modules
import {
  DAYS,
  MONTHS,
  EVENT_TYPES,
  CASE_TYPES,
  getEventTypeInfo,
} from '../components/case/caseConstants';
import { CaseDocuments } from '../components/case/CaseDocuments';
import { CaseTasks } from '../components/case/CaseTasks';
import { CaseOverview } from '../components/case/CaseOverview';
import { CaseCalendar } from '../components/case/CaseCalendar';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

// Note: Constants (DAYS, MONTHS, EVENT_TYPES, CASE_TYPES, TASK_TEMPLATES) 
// are now imported from '../components/case/caseConstants'

function CaseDetail({ user }) {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCase] = useState(null);
  const [messages, setMessages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditCaseModal, setShowEditCaseModal] = useState(false);
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [editCaseForm, setEditCaseForm] = useState({ case_name: '', case_number: '', case_type: '', client_name: '', client_email: '', client_phone: '' });
  const [editClientForm, setEditClientForm] = useState({ 
    name: '', 
    email: '', 
    phone: '',
    notification_email: true,
    notification_sms: false,
    notification_push: true
  });
  const [staffList, setStaffList] = useState([]);
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  
  // Inline messaging state
  const [newMessage, setNewMessage] = useState('');
  const [messagePriority, setMessagePriority] = useState('medium');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageAttachments, setMessageAttachments] = useState([]);
  const [mentionIds, setMentionIds] = useState([]);
  const messageTextareaRef = React.useRef(null);
  const messageFileInputRef = React.useRef(null);
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '',
    appointment_type: 'meeting',
    date: '',
    time: '09:00',
    location: '',
    notes: ''
  });
  const [savingEvent, setSavingEvent] = useState(false);
  const [calendarSearchQuery, setCalendarSearchQuery] = useState('');
  const [calendarViewMode, setCalendarViewMode] = useState('calendar'); // 'calendar' or 'list'

  // Client reminder settings state
  const [showReminderSettingsModal, setShowReminderSettingsModal] = useState(false);
  const [clientReminderSettings, setClientReminderSettings] = useState(null);
  const [loadingReminderSettings, setLoadingReminderSettings] = useState(false);
  const [savingReminderSettings, setSavingReminderSettings] = useState(false);

  const isStaffOrAdmin = user?.role === 'staff' || user?.role === 'admin';

  useEffect(() => {
    fetchCaseData();
  }, [caseId]);

  const fetchCaseData = async () => {
    try {
      const [caseRes, messagesRes, docsRes, apptsRes, tasksRes] = await Promise.all([
        fetch(`${API}/cases/${caseId}`, { credentials: 'include' }),
        fetch(`${API}/messages/case/${caseId}`, { credentials: 'include' }),
        fetch(`${API}/documents/case/${caseId}`, { credentials: 'include' }),
        fetch(`${API}/appointments/case/${caseId}`, { credentials: 'include' }),
        fetch(`${API}/tasks/case/${caseId}`, { credentials: 'include' })
      ]);

      if (caseRes.ok) {
        const caseInfo = await caseRes.json();
        setCase(caseInfo);
        // Fetch client data
        const clientRes = await fetch(`${API}/users/clients`, { credentials: 'include' });
        if (clientRes.ok) {
          const clients = await clientRes.json();
          const client = clients.find(c => c.user_id === caseInfo.client_id);
          setClientData(client);
        }
      }
      if (messagesRes.ok) setMessages(await messagesRes.json());
      if (docsRes.ok) setDocuments(await docsRes.json());
      if (apptsRes.ok) setAppointments(await apptsRes.json());
      if (tasksRes.ok) setTasks(await tasksRes.json());
      
      // Fetch staff list for task assignment
      if (user?.role !== 'client') {
        const staffRes = await fetch(`${API}/users/staff`, { credentials: 'include' });
        if (staffRes.ok) setStaffList(await staffRes.json());
      }
    } catch (error) {
      toast.error('Failed to load case');
    } finally {
      setLoading(false);
    }
  };

  // Filter messages based on search query
  const filteredMessages = React.useMemo(() => {
    const sorted = [...messages].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    if (!messageSearchQuery.trim()) return sorted;

    const query = messageSearchQuery.toLowerCase();
    return sorted.filter(msg =>
      msg.content?.toLowerCase().includes(query) ||
      msg.sender_name?.toLowerCase().includes(query)
    );
  }, [messages, messageSearchQuery]);

  // Highlight search matches in text
  const highlightMessageText = (text, isMyMessage = false) => {
    if (!messageSearchQuery.trim() || !text) return text;
    
    const query = messageSearchQuery.toLowerCase();
    const index = text.toLowerCase().indexOf(query);
    
    if (index === -1) return text;
    
    const before = text.slice(0, index);
    const match = text.slice(index, index + messageSearchQuery.length);
    const after = text.slice(index + messageSearchQuery.length);
    
    return (
      <>
        {before}
        <mark className={`${isMyMessage ? 'bg-yellow-300 text-gray-900' : 'bg-yellow-200'} px-0.5 rounded`}>
          {match}
        </mark>
        {after}
      </>
    );
  };

  // Render message content with links
  const renderMessageContent = (text, isMyMessage = false) => {
    if (!text) return null;
    
    const parts = linkifyText(text, isMyMessage);
    
    return parts.map((part, index) => {
      if (typeof part === 'string') {
        // Apply search highlighting to text parts
        return <span key={index}>{highlightMessageText(part, isMyMessage)}</span>;
      } else if (part.type === 'link') {
        // Render clickable link
        return (
          <a
            key={index}
            href={part.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`underline break-all ${isMyMessage ? 'text-white hover:text-white/80' : 'text-[#80298F] hover:text-[#6B2278]'}`}
          >
            {part.url}
          </a>
        );
      }
      return null;
    });
  };

  // Inline messaging functions
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSendingMessage(true);
    try {
      const formData = new FormData();
      formData.append('case_id', caseId);
      
      // Auto-set recipient based on user role:
      // - Clients: send to "staff_inbox" (all staff can see)
      // - Staff/Admin: send to the case's client
      if (user.role === 'client') {
        formData.append('recipient_id', 'staff_inbox');
      } else {
        formData.append('recipient_id', caseData.client_id);
      }
      
      formData.append('content', newMessage);
      formData.append('priority', messagePriority);

      if (mentionIds.length > 0) {
        formData.append('mention_ids', mentionIds.join(','));
      }
      
      // Add file attachments
      messageAttachments.forEach((file) => {
        formData.append('files', file);
      });

      const response = await fetch(`${API}/messages`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) throw new Error('Failed to send message');
      
      toast.success('Message sent successfully');
      setNewMessage('');
      setMessagePriority('medium');
      setMessageAttachments([]);
      setMentionIds([]);
      fetchCaseData(); // Refresh messages
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingMessage(false);
    }
  };

  // Download attachment with proper authentication
  const handleDownloadAttachment = async (fileId, fileName) => {
    try {
      const response = await fetch(`${API}/messages/attachment/${fileId}`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || 'Failed to download attachment';
        // Show clearer message for file not found
        if (response.status === 404) {
          toast.error('This file is no longer available. It may have been removed during a system update. Please ask the sender to re-upload.');
          return;
        }
        throw new Error(errorMessage);
      }
      
      const blob = await response.blob();
      saveAs(blob, fileName);
      toast.success('Download complete');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.message || 'Failed to download attachment');
    }
  };

  const handleMessageFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });
    
    setMessageAttachments(prev => [...prev, ...validFiles]);
    // Reset file input
    if (messageFileInputRef.current) {
      messageFileInputRef.current.value = '';
    }
  };

  const handleRemoveMessageAttachment = (index) => {
    setMessageAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getMessageFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return <ImageIcon className="h-4 w-4" />;
    } else if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
      return <FileText className="h-4 w-4" />;
    }
    return <File className="h-4 w-4" />;
  };

  const openEditCaseModal = () => {
    setEditCaseForm({
      case_name: caseData.case_name,
      case_number: caseData.case_number,
      case_type: caseData.case_type,
      client_name: caseData.client_name || '',
      client_email: caseData.client_email || '',
      client_phone: caseData.client_phone || ''
    });
    setShowEditCaseModal(true);
  };

  const handleUpdateCase = async () => {
    try {
      const response = await fetch(`${API}/cases/${caseId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editCaseForm)
      });

      if (!response.ok) throw new Error('Failed to update case');
      toast.success('Case updated successfully');
      setShowEditCaseModal(false);
      fetchCaseData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openEditClientModal = () => {
    // Use clientData if available (registered client), otherwise use case data
    if (clientData) {
      setEditClientForm({
        name: clientData.name || '',
        email: clientData.email || '',
        phone: clientData.phone || '',
        notification_email: clientData.notification_email !== false,
        notification_sms: clientData.notification_sms || false,
        notification_push: clientData.notification_push !== false
      });
      setShowEditClientModal(true);
    } else if (caseData) {
      // Client hasn't registered yet - use case data for editing
      setEditClientForm({
        name: caseData.client_name || '',
        email: caseData.client_email || '',
        phone: caseData.client_phone || '',
        notification_email: true,
        notification_sms: false,
        notification_push: true
      });
      setShowEditClientModal(true);
    } else {
      toast.error('Unable to load client information');
    }
  };

  const handleUpdateClient = async () => {
    try {
      // If client hasn't registered (no clientData), update the case's client info instead
      if (!clientData) {
        // Update case with new client info
        const response = await fetch(`${API}/cases/${caseData.case_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            client_name: editClientForm.name,
            client_email: editClientForm.email,
            client_phone: editClientForm.phone
          })
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to update client information');
        }
        toast.success('Client information updated successfully');
        setShowEditClientModal(false);
        fetchCaseData();
        return;
      }

      // Client is registered - update their user record
      // Update name
      if (editClientForm.name !== clientData.name) {
        const nameResponse = await fetch(`${API}/users/${caseData.client_id}/name`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: editClientForm.name })
        });
        if (!nameResponse.ok) {
          const error = await nameResponse.json();
          throw new Error(error.detail || 'Failed to update name');
        }
      }

      // Update email
      if (editClientForm.email !== clientData.email) {
        const emailResponse = await fetch(`${API}/users/${caseData.client_id}/email`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email: editClientForm.email })
        });
        if (!emailResponse.ok) {
          const error = await emailResponse.json();
          throw new Error(error.detail || 'Failed to update email');
        }
      }

      // Update phone
      if (editClientForm.phone !== clientData.phone) {
        const phoneResponse = await fetch(`${API}/users/${caseData.client_id}/phone`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ phone: editClientForm.phone })
        });
        if (!phoneResponse.ok) {
          const error = await phoneResponse.json();
          throw new Error(error.detail || 'Failed to update phone');
        }
      }

      // Update notification preferences
      const notifResponse = await fetch(`${API}/users/${caseData.client_id}/notification-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          notification_email: editClientForm.notification_email,
          notification_sms: editClientForm.notification_sms,
          notification_push: editClientForm.notification_push
        })
      });
      if (!notifResponse.ok) {
        const error = await notifResponse.json();
        throw new Error(error.detail || 'Failed to update notification preferences');
      }

      toast.success('Client details updated successfully');
      setShowEditClientModal(false);
      fetchCaseData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateClientName = async () => {
    try {
      const response = await fetch(`${API}/users/${caseData.client_id}/name`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: editClientForm.name })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update name');
      }
      toast.success('Client name updated');
      fetchCaseData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateClientEmail = async () => {
    try {
      const response = await fetch(`${API}/users/${caseData.client_id}/email`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: editClientForm.email })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update email');
      }
      toast.success('Client email updated');
      setShowEditClientModal(false);
      fetchCaseData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Client Reminder Settings Functions
  const fetchClientReminderSettings = async (clientId) => {
    setLoadingReminderSettings(true);
    try {
      const response = await fetch(`${API}/admin/clients/${clientId}/reminder-settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setClientReminderSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch reminder settings:', error);
      toast.error('Failed to load reminder settings');
    } finally {
      setLoadingReminderSettings(false);
    }
  };

  const openReminderSettingsModal = () => {
    if (caseData?.client_id) {
      fetchClientReminderSettings(caseData.client_id);
      setShowReminderSettingsModal(true);
    }
  };

  const saveClientReminderSettings = async () => {
    if (!caseData?.client_id) return;
    
    setSavingReminderSettings(true);
    try {
      const response = await fetch(`${API}/admin/clients/${caseData.client_id}/reminder-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: caseData.client_id,
          ...clientReminderSettings
        })
      });
      
      if (response.ok) {
        toast.success('Client reminder settings saved');
        setShowReminderSettingsModal(false);
      } else {
        toast.error('Failed to save reminder settings');
      }
    } catch (error) {
      console.error('Failed to save reminder settings:', error);
      toast.error('Failed to save reminder settings');
    } finally {
      setSavingReminderSettings(false);
    }
  };

  const resetClientReminderSettings = async () => {
    if (!caseData?.client_id) return;
    
    try {
      const response = await fetch(`${API}/admin/clients/${caseData.client_id}/reminder-settings`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Client reset to firm defaults');
        setClientReminderSettings({ using_firm_defaults: true, enabled: true });
      }
    } catch (error) {
      console.error('Failed to reset reminder settings:', error);
      toast.error('Failed to reset settings');
    }
  };

  const updateReminderCategory = (category, field, value) => {
    setClientReminderSettings(prev => {
      // If using firm defaults, initialize with custom settings
      if (prev?.using_firm_defaults) {
        return {
          ...prev,
          using_firm_defaults: false,
          [category]: { enabled: true, [field]: value }
        };
      }
      return {
        ...prev,
        [category]: {
          ...(prev?.[category] || {}),
          [field]: value
        }
      };
    });
  };

  const handleLogout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/login');
  };

  // Calendar helper functions
  
  // Helper to parse date strings consistently - handles both with and without "Z" suffix
  const parseAppointmentDate = (dateStr) => {
    if (!dateStr) return new Date();
    // If the date string doesn't end with Z and doesn't have timezone info, treat it as UTC
    const str = String(dateStr);
    if (!str.endsWith('Z') && !str.includes('+') && !str.includes('-', 10)) {
      return new Date(str + 'Z');
    }
    return new Date(str);
  };

  // Calendar event handlers
  const openCreateEventModal = (preSelectedDate = null) => {
    const dateToUse = preSelectedDate || selectedDate || new Date();
    const year = dateToUse.getFullYear();
    const month = String(dateToUse.getMonth() + 1).padStart(2, '0');
    const day = String(dateToUse.getDate()).padStart(2, '0');
    
    setEventForm({
      title: '',
      appointment_type: 'meeting',
      date: `${year}-${month}-${day}`,
      time: '09:00',
      location: '',
      notes: ''
    });
    setShowCreateEventModal(true);
  };

  const openEditEventModal = (appointment) => {
    const aptDate = parseAppointmentDate(appointment.date);
    
    // Convert to Pacific time for edit form display
    const pacificDateStr = aptDate.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
    const pacificTimeStr = aptDate.toLocaleTimeString('en-GB', { 
      timeZone: 'America/Los_Angeles',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    setEditingAppointment(appointment);
    setEventForm({
      title: appointment.title,
      appointment_type: appointment.appointment_type,
      date: pacificDateStr,
      time: pacificTimeStr,
      location: appointment.location || '',
      notes: appointment.notes || ''
    });
    setShowEditEventModal(true);
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title || !eventForm.date || !eventForm.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingEvent(true);
    try {
      // Send as naive datetime string - backend treats it as Pacific time
      const dateTimeStr = `${eventForm.date}T${eventForm.time}:00`;
      
      const response = await fetch(`${API}/appointments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseId,
          title: eventForm.title,
          appointment_type: eventForm.appointment_type,
          date: dateTimeStr,
          location: eventForm.location || null,
          notes: eventForm.notes || null,
          attendees: []
        })
      });

      if (!response.ok) throw new Error('Failed to create event');
      toast.success('Event created successfully');
      setShowCreateEventModal(false);
      fetchCaseData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingEvent(false);
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title || !eventForm.date || !eventForm.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingEvent(true);
    try {
      // Send as naive datetime string - backend treats it as Pacific time
      const dateTimeStr = `${eventForm.date}T${eventForm.time}:00`;
      const params = new URLSearchParams();
      params.append('title', eventForm.title);
      params.append('appointment_type', eventForm.appointment_type);
      params.append('date', dateTimeStr);
      if (eventForm.location) params.append('location', eventForm.location);
      if (eventForm.notes) params.append('notes', eventForm.notes);

      const response = await fetch(`${API}/appointments/${editingAppointment.appointment_id}?${params.toString()}`, {
        method: 'PATCH',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to update event');
      toast.success('Event updated successfully');
      setShowEditEventModal(false);
      setEditingAppointment(null);
      fetchCaseData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeleteEvent = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      const response = await fetch(`${API}/appointments/${appointmentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete event');
      toast.success('Event deleted successfully');
      fetchCaseData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  if (!caseData) {
    return <div className="min-h-screen flex items-center justify-center"><p>Case not found</p></div>;
  }

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />
      
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900" data-testid="case-title">{caseData.case_name}</h1>
              <p className="text-gray-600 mt-1 font-mono">{caseData.case_number}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="overview-tab"
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'messages'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="messages-tab"
            >
              Messages ({messages.length})
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'documents'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="documents-tab"
            >
              Documents ({documents.length})
            </button>
            <button
              onClick={() => setActiveTab('calendar')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'calendar'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="calendar-tab"
            >
              Calendar ({appointments.length})
            </button>
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Case Overview Component - handles case info, status, and milestones */}
            <CaseOverview
              caseId={caseId}
              caseData={caseData}
              clientData={clientData}
              user={user}
              onRefresh={fetchCaseData}
              isStaffOrAdmin={isStaffOrAdmin}
              onEditCase={openEditCaseModal}
              onEditClient={openEditClientModal}
              onOpenReminderSettings={openReminderSettingsModal}
            />

            {/* Tasks Section - Using extracted component */}
            <div className="mt-6">
              <CaseTasks
                caseId={caseId}
                caseData={caseData}
                tasks={tasks}
                clientData={clientData}
                staffList={staffList}
                user={user}
                onRefresh={fetchCaseData}
                isStaffOrAdmin={isStaffOrAdmin}
              />
            </div>
          </>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="messages-content">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Conversation</h2>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search messages..."
                  value={messageSearchQuery}
                  onChange={(e) => setMessageSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="case-message-search-input"
                />
                {messageSearchQuery && (
                  <button
                    onClick={() => setMessageSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            {messageSearchQuery && (
              <p className="text-sm text-gray-500 mb-3">
                {filteredMessages.length} {filteredMessages.length === 1 ? 'result' : 'results'} found
              </p>
            )}
            
            {/* Messages List */}
            {filteredMessages.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {messageSearchQuery 
                    ? `No messages matching "${messageSearchQuery}"` 
                    : 'No messages yet. Start the conversation!'}
                </p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[400px] overflow-y-auto mb-6">
                {filteredMessages.map(msg => {
                  // For staff/admin viewing: staff messages on right, client messages on left
                  // For client viewing: client's own messages on right, staff messages on left
                  const isStaffOrAdminMessage = msg.sender_role === 'staff' || msg.sender_role === 'admin';
                  const isMyMessage = user.role === 'client' 
                    ? msg.sender_id === user.user_id  // Clients see their own messages on right
                    : isStaffOrAdminMessage;  // Staff/Admin see staff messages on right
                  return (
                    <div
                      key={msg.message_id}
                      className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] rounded-lg p-4 ${
                        isMyMessage 
                          ? 'bg-[#80298F] text-white' 
                          : 'bg-gray-100 text-gray-900'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className={`text-sm font-semibold ${isMyMessage ? 'text-white' : 'text-gray-900'}`}>
                              {msg.sender_id === user.user_id ? 'You' : highlightMessageText(msg.sender_name, isMyMessage)}
                            </p>
                            <p className={`text-xs ${isMyMessage ? 'text-white/70' : 'text-gray-500'}`}>
                              {msg.sender_role} • {msg.formatted_time || formatDateTimeShort(msg.created_at)}
                            </p>
                          </div>
                          {msg.priority !== 'medium' && (
                            <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${
                              isMyMessage ? 'bg-white/20 text-white' : msg.priority === 'urgent' ? 'text-[#DC3239] bg-[#DC3239]/10' : 'text-gray-600 bg-gray-200'
                            }`}>
                              {msg.priority}
                            </span>
                          )}
                        </div>
                        <p className={`text-sm whitespace-pre-wrap break-words overflow-hidden ${isMyMessage ? 'text-white' : 'text-gray-700'}`} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                          {renderMessageContent(msg.content, isMyMessage)}
                        </p>
                        
                        {/* Attachments */}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {msg.attachments.map((attachment, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleDownloadAttachment(attachment.file_id, attachment.file_name || attachment.filename)}
                                data-testid={`download-attachment-${attachment.file_id}`}
                                className={`flex items-center space-x-2 p-2 rounded border w-full text-left ${
                                  isMyMessage 
                                    ? 'bg-white/10 border-white/20 hover:bg-white/20' 
                                    : 'bg-white border-gray-200 hover:bg-gray-50'
                                } transition-colors cursor-pointer`}
                              >
                                <Download className={`h-4 w-4 ${isMyMessage ? 'text-white' : 'text-gray-500'}`} />
                                <span className={`text-xs flex-1 truncate ${isMyMessage ? 'text-white' : 'text-gray-700'}`}>
                                  {attachment.file_name || attachment.filename}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex items-center justify-end mt-2">
                          <span className={`text-xs ${isMyMessage ? 'text-white/70' : 'text-gray-400'}`}>
                            ✓✓
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Inline Compose Message Form */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-md font-semibold text-gray-900 mb-4">Send a Message</h3>
              <form onSubmit={handleSendMessage} className="space-y-4">
                {/* Show who the message will be sent to */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
                      {user.role === 'client' 
                        ? 'The Ramirez Legal Group Team' 
                        : clientData?.name || 'Client'}
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <label htmlFor="msg-priority" className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <Select value={messagePriority} onValueChange={setMessagePriority}>
                      <SelectTrigger id="msg-priority" data-testid="inline-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label htmlFor="msg-content" className="block text-sm font-medium text-gray-700 mb-1">
                    Message {(user.role === 'staff' || user.role === 'admin') && <span className="text-gray-400 font-normal">- Type @ to mention staff</span>}
                  </label>
                  {(user.role === 'staff' || user.role === 'admin') ? (
                    <MentionInput
                      value={newMessage}
                      onChange={setNewMessage}
                      onMentionsChange={setMentionIds}
                      placeholder="Type your message here... Use @name to mention staff"
                      rows={3}
                      disabled={sendingMessage}
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                    />
                  ) : (
                    <Textarea
                      id="msg-content"
                      ref={messageTextareaRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message here..."
                      rows={3}
                      required
                      data-testid="inline-message-textarea"
                      className="text-sm"
                    />
                  )}
                </div>

                {/* File Attachments */}
                <div>
                  <input
                    ref={messageFileInputRef}
                    type="file"
                    multiple
                    onChange={handleMessageFileSelect}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    data-testid="inline-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => messageFileInputRef.current?.click()}
                    data-testid="inline-attach-file-button"
                  >
                    <Paperclip className="h-4 w-4 mr-2" />
                    Attach Files
                  </Button>
                  
                  {messageAttachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {messageAttachments.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
                        >
                          <div className="flex items-center space-x-2 flex-1">
                            {getMessageFileIcon(file.name)}
                            <span className="text-sm text-gray-700 truncate">{file.name}</span>
                            <span className="text-xs text-gray-500">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveMessageAttachment(index)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            data-testid={`inline-remove-attachment-${index}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <Button
                    type="submit"
                    className="bg-[#80298F] hover:bg-[#6B2278]"
                    disabled={sendingMessage}
                    data-testid="inline-send-message-button"
                  >
                    {sendingMessage ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate('/messages')}
                    className="text-[#80298F] hover:text-[#6B2278]"
                  >
                    Open Full Messages Page
                  </Button>
                </div>

                {messagePriority === 'urgent' && (
                  <div className="p-3 bg-[#DC3239]/10 border border-[#DC3239]/20 rounded-lg flex items-start space-x-2">
                    <AlertCircle className="h-5 w-5 text-[#DC3239] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#DC3239]">
                      This message will be marked as urgent and may trigger immediate notifications.
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>
        )}

        {/* Documents Tab - Using extracted component */}
        {activeTab === 'documents' && (
          <CaseDocuments
            caseId={caseId}
            documents={documents}
            user={user}
            onRefresh={fetchCaseData}
            isStaffOrAdmin={isStaffOrAdmin}
          />
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <CaseCalendar
            appointments={appointments}
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            calendarSearchQuery={calendarSearchQuery}
            setCalendarSearchQuery={setCalendarSearchQuery}
            calendarViewMode={calendarViewMode}
            setCalendarViewMode={setCalendarViewMode}
            isStaffOrAdmin={isStaffOrAdmin}
            onCreateEvent={openCreateEventModal}
            onEditEvent={openEditEventModal}
            onDeleteEvent={handleDeleteEvent}
            parseAppointmentDate={parseAppointmentDate}
          />
        )}
      </main>

      {/* Create Event Modal */}
      <Dialog open={showCreateEventModal} onOpenChange={setShowCreateEventModal}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Create Calendar Event</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEvent} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-title">Event Title *</Label>
              <Input
                id="create-title"
                value={eventForm.title}
                onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                placeholder="e.g., Client Meeting"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-type">Event Type *</Label>
              <Select 
                value={eventForm.appointment_type} 
                onValueChange={(value) => setEventForm({...eventForm, appointment_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded ${type.color.split(' ')[0]}`}></span>
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-date">Date *</Label>
                <Input
                  id="create-date"
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-time">Time *</Label>
                <Input
                  id="create-time"
                  type="time"
                  value={eventForm.time}
                  onChange={(e) => setEventForm({...eventForm, time: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-location">Location / Meeting Link (Optional)</Label>
              <Input
                id="create-location"
                value={eventForm.location}
                onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                placeholder="e.g., Conference Room A or https://zoom.us/j/..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-notes">Notes (Optional)</Label>
              <Textarea
                id="create-notes"
                value={eventForm.notes}
                onChange={(e) => setEventForm({...eventForm, notes: e.target.value})}
                placeholder="Add any additional notes..."
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateEventModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingEvent} className="bg-[#80298F] hover:bg-[#6b2278]">
                {savingEvent ? 'Creating...' : 'Create Event'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Event Modal */}
      <Dialog open={showEditEventModal} onOpenChange={setShowEditEventModal}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Edit Calendar Event</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateEvent} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Event Title *</Label>
              <Input
                id="edit-title"
                value={eventForm.title}
                onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                placeholder="e.g., Client Meeting"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-type">Event Type *</Label>
              <Select 
                value={eventForm.appointment_type} 
                onValueChange={(value) => setEventForm({...eventForm, appointment_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded ${type.color.split(' ')[0]}`}></span>
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-date">Date *</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-time">Time *</Label>
                <Input
                  id="edit-time"
                  type="time"
                  value={eventForm.time}
                  onChange={(e) => setEventForm({...eventForm, time: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-location">Location / Meeting Link (Optional)</Label>
              <Input
                id="edit-location"
                value={eventForm.location}
                onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                placeholder="e.g., Conference Room A or https://zoom.us/j/..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes (Optional)</Label>
              <Textarea
                id="edit-notes"
                value={eventForm.notes}
                onChange={(e) => setEventForm({...eventForm, notes: e.target.value})}
                placeholder="Add any additional notes..."
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowEditEventModal(false);
                setEditingAppointment(null);
              }}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingEvent} className="bg-[#80298F] hover:bg-[#6b2278]">
                {savingEvent ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Case Modal */}
      <Dialog open={showEditCaseModal} onOpenChange={setShowEditCaseModal}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Case & Client Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Case Details Section */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm border-b pb-2">Case Details</h4>
              <div>
                <Label>Case Name *</Label>
                <Input
                  value={editCaseForm.case_name}
                  onChange={(e) => setEditCaseForm({...editCaseForm, case_name: e.target.value})}
                  placeholder="Case name"
                />
              </div>
              <div>
                <Label>Case ID</Label>
                <Input
                  value={editCaseForm.case_number}
                  onChange={(e) => setEditCaseForm({...editCaseForm, case_number: e.target.value})}
                  placeholder="Case ID"
                />
              </div>
              <div>
                <Label>Case Type *</Label>
                <Select
                  value={editCaseForm.case_type}
                  onValueChange={(value) => setEditCaseForm({...editCaseForm, case_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CASE_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Client Details Section */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm border-b pb-2">Client Details</h4>
              <div>
                <Label>Client Name *</Label>
                <Input
                  value={editCaseForm.client_name}
                  onChange={(e) => setEditCaseForm({...editCaseForm, client_name: e.target.value})}
                  placeholder="Client name"
                />
              </div>
              <div>
                <Label>Client Email *</Label>
                <Input
                  type="email"
                  value={editCaseForm.client_email}
                  onChange={(e) => setEditCaseForm({...editCaseForm, client_email: e.target.value})}
                  placeholder="client@example.com"
                />
              </div>
              <div>
                <Label>Client Phone *</Label>
                <Input
                  type="tel"
                  value={formatPhoneNumber(editCaseForm.client_phone)}
                  onChange={(e) => setEditCaseForm({...editCaseForm, client_phone: formatPhoneInput(e.target.value)})}
                  placeholder="(310) 567-7777"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditCaseModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateCase} className="bg-[#80298F] hover:bg-[#6b2278]">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Client Modal - keeping for backwards compatibility */}
      <Dialog open={showEditClientModal} onOpenChange={setShowEditClientModal}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Client Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Name */}
            <div>
              <Label htmlFor="client-name">Full Name</Label>
              <Input
                id="client-name"
                value={editClientForm.name}
                onChange={(e) => setEditClientForm({...editClientForm, name: e.target.value})}
                placeholder="Client name"
                className="mt-1"
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="client-email">Email Address</Label>
              <Input
                id="client-email"
                type="email"
                value={editClientForm.email}
                onChange={(e) => setEditClientForm({...editClientForm, email: e.target.value})}
                placeholder="client@example.com"
                className="mt-1"
              />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="client-phone">Phone Number</Label>
              <Input
                id="client-phone"
                type="tel"
                value={formatPhoneNumber(editClientForm.phone)}
                onChange={(e) => setEditClientForm({...editClientForm, phone: formatPhoneInput(e.target.value)})}
                placeholder="(310) 567-7777"
                className="mt-1"
              />
            </div>

            {/* Notification Preferences Section - only show for registered clients */}
            {clientData && (
              <div className="border-t pt-4 mt-4">
                <Label className="text-base font-semibold mb-3 block">Notification Preferences</Label>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Email Notifications</span>
                    </div>
                    <Switch
                      checked={editClientForm.notification_email}
                      onCheckedChange={(checked) => setEditClientForm({...editClientForm, notification_email: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">SMS Notifications</span>
                    </div>
                    <Switch
                      checked={editClientForm.notification_sms}
                      onCheckedChange={(checked) => setEditClientForm({...editClientForm, notification_sms: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Push Notifications</span>
                    </div>
                    <Switch
                      checked={editClientForm.notification_push}
                      onCheckedChange={(checked) => setEditClientForm({...editClientForm, notification_push: checked})}
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Info message for unregistered clients */}
            {!clientData && (
              <p className="text-sm text-gray-500 italic border-t pt-4 mt-4">
                Note: This client has not registered yet. Notification preferences can be set after they create their account.
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditClientModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateClient} className="bg-[#80298F] hover:bg-[#6b2278]">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Reminder Settings Modal */}
      <Dialog open={showReminderSettingsModal} onOpenChange={setShowReminderSettingsModal}>
        <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-[#80298F]" />
              Client Reminder Settings
            </DialogTitle>
          </DialogHeader>
          
          {loadingReminderSettings ? (
            <div className="py-8 text-center text-gray-500">Loading settings...</div>
          ) : clientReminderSettings ? (
            <div className="space-y-6 py-4">
              {clientReminderSettings.using_firm_defaults ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Using Firm Defaults</strong><br />
                    This client is using the firm-wide reminder settings. Customize below to create client-specific overrides.
                  </p>
                </div>
              ) : (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex justify-between items-center">
                  <p className="text-sm text-purple-800">
                    <strong>Custom Settings Active</strong>
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={resetClientReminderSettings}
                    className="text-xs"
                  >
                    Reset to Defaults
                  </Button>
                </div>
              )}

              {/* Master Enable */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label className="font-medium">Enable Reminders</Label>
                  <p className="text-xs text-gray-500">Turn off to disable all reminders for this client</p>
                </div>
                <Switch
                  checked={clientReminderSettings.enabled !== false}
                  onCheckedChange={(checked) => setClientReminderSettings(prev => ({ ...prev, enabled: checked, using_firm_defaults: false }))}
                />
              </div>

              {clientReminderSettings.enabled !== false && (
                <>
                  {/* Event Reminders */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-medium flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-blue-500" />
                        Calendar Events
                      </Label>
                      <Switch
                        checked={clientReminderSettings.events?.enabled !== false}
                        onCheckedChange={(checked) => updateReminderCategory('events', 'enabled', checked)}
                      />
                    </div>
                    <p className="text-xs text-gray-500">Reminders for appointments, hearings, meetings</p>
                  </div>

                  {/* Upcoming Task Reminders */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-medium flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Upcoming Tasks
                      </Label>
                      <Switch
                        checked={clientReminderSettings.upcoming_tasks?.enabled !== false}
                        onCheckedChange={(checked) => updateReminderCategory('upcoming_tasks', 'enabled', checked)}
                      />
                    </div>
                    <p className="text-xs text-gray-500">Reminders before task due dates</p>
                  </div>

                  {/* Overdue Task Reminders */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-medium flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-orange-500" />
                        Overdue Tasks
                      </Label>
                      <Switch
                        checked={clientReminderSettings.overdue_tasks?.enabled !== false}
                        onCheckedChange={(checked) => updateReminderCategory('overdue_tasks', 'enabled', checked)}
                      />
                    </div>
                    <p className="text-xs text-gray-500">Persistent reminders for past-due tasks</p>
                  </div>

                  {/* Unread Message Reminders */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-medium flex items-center gap-2">
                        <FileText className="h-4 w-4 text-purple-500" />
                        Unread Messages
                      </Label>
                      <Switch
                        checked={clientReminderSettings.unread_messages?.enabled !== false}
                        onCheckedChange={(checked) => updateReminderCategory('unread_messages', 'enabled', checked)}
                      />
                    </div>
                    <p className="text-xs text-gray-500">Reminders for messages client hasn&apos;t read</p>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">Unable to load settings</div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowReminderSettingsModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveClientReminderSettings} 
              disabled={savingReminderSettings}
              className="bg-[#80298F] hover:bg-[#80298F]/90"
            >
              {savingReminderSettings ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CaseDetail;